if (!customElements.get('variant-selects')) {
  customElements.define(
    'variant-selects',
    class VariantSelects extends HTMLElement {
      constructor() {
        super();
      }

      get selectedOptionValues() {
        return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
          ({ dataset }) => dataset.optionValueId
        );
      }

      getInputForEventTarget(target) {
        return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
      }

      // Returns the full set of currently selected option values, one per option position,
      // read directly from the DOM (so it reflects the value the user just clicked/selected).
      get currentOptionValuesByPosition() {
        const positions = Array.from(this.querySelectorAll('[data-option-position]'))
          .map((el) => parseInt(el.dataset.optionPosition, 10))
          .filter((position) => !Number.isNaN(position))
          .sort((a, b) => a - b);

        return [...new Set(positions)].map((position) => {
          const fieldset = this.querySelector(`[data-option-position="${position}"]`);
          const select = fieldset?.querySelector('select');
          const checkedInput = fieldset?.querySelector('input:checked');
          const value = select ? select.value : checkedInput ? checkedInput.value : null;
          return { position, value };
        });
      }

      get variantsData() {
        if (this._variantsData) return this._variantsData;
        const script = this.querySelector('[data-product-variants]');
        try {
          this._variantsData = script ? JSON.parse(script.textContent) : [];
        } catch (error) {
          this._variantsData = [];
        }
        return this._variantsData;
      }

      // Given the option position that just changed, find the best variant to select:
      // 1. An available variant matching every currently selected option value.
      // 2. If that combination doesn't exist (or is unavailable), an available variant that
      //    keeps the just-changed value and matches as many of the other selected values as possible.
      // 3. If no available variant contains the just-changed value at all, fall back to any
      //    variant (even unavailable) that keeps the changed value, so the picker doesn't jump
      //    to an unrelated combination.
      resolveVariantForChange(changedPosition) {
        const variants = this.variantsData;
        if (!variants || !variants.length) return null;

        const desired = this.currentOptionValuesByPosition;
        if (!desired.length) return null;

        const optionKey = (position) => `option${position}`;
        const matchesAll = (variant) => desired.every(({ position, value }) => variant[optionKey(position)] === value);

        const exactMatch = variants.find((variant) => variant.available && matchesAll(variant));
        if (exactMatch) return exactMatch;

        const changedValue = desired.find(({ position }) => position === changedPosition)?.value;
        if (changedValue == null) return null;

        const matchesChanged = (variant) => variant[optionKey(changedPosition)] === changedValue;
        const scoreVariant = (variant) =>
          desired.filter(({ position, value }) => position !== changedPosition && variant[optionKey(position)] === value)
            .length;

        const candidates = variants.filter(matchesChanged);
        const availableCandidates = candidates.filter((variant) => variant.available);
        const pool = availableCandidates.length ? availableCandidates : candidates;
        if (!pool.length) return null;

        return pool.reduce((best, variant) =>
          scoreVariant(variant) > scoreVariant(best) ? variant : best
        , pool[0]);
      }

      connectedCallback() {
        this.addEventListener('change', (event) => {
          const target = this.getInputForEventTarget(event.target);
          this.updateSelectedSwatchValue(event);

          const changedFieldset = event.target.closest('[data-option-position]');
          const changedPosition = changedFieldset ? parseInt(changedFieldset.dataset.optionPosition, 10) : null;
          const resolvedVariant = changedPosition ? this.resolveVariantForChange(changedPosition) : null;

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
            data: {
              event,
              target,
              selectedOptionValues: this.selectedOptionValues,
              resolvedVariantId: resolvedVariant?.id ?? null,
            },
          });
        });
      }

      updateSelectedSwatchValue({ target }) {
        const { value, tagName } = target;

        if (tagName === 'SELECT' && target.selectedOptions.length) {
          Array.from(target.options)
            .find((option) => option.getAttribute('selected'))
            .removeAttribute('selected');
          target.selectedOptions[0].setAttribute('selected', 'selected');

          const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
          const selectedDropdownSwatchValue = target
            .closest('.product-form__input')
            .querySelector('[data-selected-value] > .swatch');
          if (!selectedDropdownSwatchValue) return;
          if (swatchValue) {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
            selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
          } else {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
            selectedDropdownSwatchValue.classList.add('swatch--unavailable');
          }

          selectedDropdownSwatchValue.style.setProperty(
            '--swatch-focal-point',
            target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
          );
        } else if (tagName === 'INPUT' && target.type === 'radio') {
          const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
          if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
        }
      }
    }
  );
}
